import { Platform } from 'react-native';
import { Opportunity } from '../types';

/**
 * Utility functions for tracking analytics events across the application
 */
interface EventParams {
  event_category?: string;
  event_label?: string;
  [key: string]: any;
}

/** 
 * Track a custom event in Google Analytics
 */
export const trackEvent = (eventName: string, params: EventParams = {}): void => {
  if (Platform.OS === 'web' && typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', eventName, {
      event_category: params.event_category || 'General',
      event_label: params.event_label || '',
      ...params
    });
    console.log(`Tracked event: ${eventName}`, params);
  } else {
    // For non-web platforms, log but don't track
    console.log(`[Analytics] Event would be tracked on web: ${eventName}`, params);
  }
};

/**
 * Track opportunity card click
 */
export const trackOpportunityCardClick = (opportunity: Opportunity): void => {
  const primaryType = Array.isArray(opportunity.type) 
    ? opportunity.type[0] 
    : opportunity.type.split(',')[0].trim();

  trackEvent('opportunity_card_click', {
    event_category: 'Engagement',
    event_label: opportunity.title,
    opportunity_id: opportunity.id,
    opportunity_type: primaryType,
    opportunity_org: opportunity.organization
  });
};

/**
 * Track "Apply Now" button click
 */
export const trackApplyNowClick = (opportunity: Opportunity): void => {
  const primaryType = Array.isArray(opportunity.type) 
    ? opportunity.type[0] 
    : opportunity.type.split(',')[0].trim();

  trackEvent('apply_now_click', {
    event_category: 'Engagement',
    event_label: opportunity.title,
    opportunity_id: opportunity.id, 
    opportunity_type: primaryType,
    opportunity_org: opportunity.organization,
    has_website: !!opportunity.website
  });
};

/**
 * Track share action
 */
export const trackShare = (opportunity: Opportunity, shareMethod: string = 'share_button'): void => {
  const primaryType = Array.isArray(opportunity.type) 
    ? opportunity.type[0] 
    : opportunity.type.split(',')[0].trim();

  trackEvent('share', {
    event_category: 'Engagement',
    event_label: opportunity.title,
    opportunity_id: opportunity.id,
    opportunity_type: primaryType,
    opportunity_org: opportunity.organization,
    share_method: shareMethod
  });
};

/**
 * Track filter usage
 */
export const trackFilterUse = (filterType: string, filterValue: string | string[]): void => {
  const values = Array.isArray(filterValue) ? filterValue : [filterValue];
  
  trackEvent('filter_use', {
    event_category: 'Search',
    event_label: `${filterType}: ${values.join(', ')}`,
    filter_type: filterType,
    filter_values: values
  });
};

/**
 * Track search queries
 */
export const trackSearch = (query: string): void => {
  trackEvent('search', {
    event_category: 'Search',
    event_label: query,
    search_term: query
  });
};

/**
 * Track view changes (grid/calendar)
 */
export const trackViewChange = (viewMode: string): void => {
  trackEvent('view_change', {
    event_category: 'UI',
    event_label: viewMode,
    view_mode: viewMode
  });
};

/**
 * Track page view (for route changes)
 */
export const trackPageView = (screen: string, path?: string): void => {
  if (Platform.OS === 'web' && typeof window !== 'undefined' && (window as any).gtag) {
    const pageLocation = path || window.location.pathname;
    (window as any).gtag('config', process.env.ANALYTIC_KEY, {
      page_title: screen,
      page_path: pageLocation
    });
    console.log(`Tracked page view: ${screen}`, { path: pageLocation });
  } else {
    console.log(`[Analytics] Page view would be tracked on web: ${screen}`);
  }
};